import React from 'react';
import { ChatMessage } from '../types';

import { useTypewriter, Cursor, Typewriter } from 'react-simple-typewriter';

import DOMPurify from 'dompurify';

interface ChatHistoryProps {
  messages: ChatMessage[];
}

interface MarkupMessageRendererProps {
  message: string;
}

const MarkupMessageRenderer: React.FC<MarkupMessageRendererProps> = ({ message }) => {
  const parseMarkup = (text: string) => {
    if (!text) return '';
    
    // First, escape any existing HTML to prevent injection
    const escapedText = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;');
    
    // Replace markup symbols with HTML tags
    let parsedText = escapedText
      // Bold: **text** or __text__
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/__(.*?)__/g, '<strong>$1</strong>')
      
      // Italic: *text* or _text_
      .replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<em>$1</em>')
      .replace(/(?<!_)_([^_]+)_(?!_)/g, '<em>$1</em>')
      
      // Strikethrough: ~~text~~
      .replace(/~~(.*?)~~/g, '<del>$1</del>')
      
      // Code inline: `code`
      .replace(/`([^`]+)`/g, '<code class="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono">$1</code>')
      
      // Code blocks: ```code```
      .replace(/```([\s\S]*?)```/g, '<pre class="bg-gray-100 p-3 rounded my-2 overflow-x-auto"><code class="font-mono text-sm">$1</code></pre>')
      
      // Links: [text](url) - with URL validation
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, linkText, url) => {
        // Basic URL validation
        const urlPattern = /^https?:\/\//i;
        const safeUrl = urlPattern.test(url) ? url : '#';
        return `<a href="${safeUrl}" class="text-blue-600 hover:text-blue-800 underline" target="_blank" rel="noopener noreferrer">${linkText}</a>`;
      })
      
      // Line breaks
      .replace(/\n/g, '<br />');
    
    // Configure DOMPurify with allowed tags and attributes
    const cleanHTML = DOMPurify.sanitize(parsedText, {
      ALLOWED_TAGS: ['strong', 'em', 'del', 'code', 'pre', 'br', 'a'],
      ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
      ALLOW_DATA_ATTR: false,
      FORBID_TAGS: ['script', 'object', 'embed', 'base', 'link'],
      FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover']
    });
    return cleanHTML;
  };

  return (
    <div 
      className="message-content"
      dangerouslySetInnerHTML={{ __html: parseMarkup(message) }}
    />
  );
};


const ChatHistory: React.FC<ChatHistoryProps> = ({ messages }) => {
  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className={`chat-history-container ${messages.length > 4 ? 'overflow-active' : ''}`}>
      {messages.length === 0 ? (
        <div className="chat-no-history">
          Furnichanter AI at your service - share what furniture you're looking for!
          {/* Hi! How can I help you find furnitufurniture today? */}
        </div>
      ) : (
        <div className="chat-messages-container">
          {messages.map((message) => (
            <>  {/* style={{ display: 'flex', flexDirection: 'column', gap: '0.7rem' }}> */}
              <div 
                key={message.id} 
                className="chat-message-container"
                style={{ marginLeft: 'calc(30% - 1.5rem)', borderRadius: '8px 8px 0 8px' }}
              >
                <div className="chat-message-time">
                  {formatTime(message.timestamp)}
                </div>
                
                <div
                  className="chat-message-query"
                  style={{ marginBottom: message.response ? '8px' : '0' }}
                >
                  {/* {message.query} */}
                  <MarkupMessageRenderer message={message.query} />
                </div>
                
                {!message.response && (
                  <div className="chat-message-processing">
                    ⏳ Processing...
                  </div>
                )}
              </div>

              {message.response?.text && (
                <div
                  key={message.id} 
                  className="chat-message-container"
                  style={{ borderRadius: '8px 8px 8px 0 ' }}
                >
                  <div className="chat-message-time">
                    {formatTime(message.timestamp)}
                  </div>
                  
                  <div
                    className="chat-message-query"
                    // style={{ marginBottom: message.response ? '8px' : '0' }}
                  >
                    
                    {/* {messages[messages.length - 1]?.id === message.id ? (
                      <Typewriter
                        key={message.id}
                        words={[message.response.text]}
                        loop={false}
                        cursor={true}
                        cursorStyle='|'
                        typeSpeed={30}
                        deleteSpeed={0}
                        cursorBlinking={false}
                      />
                    ) : ( */}
                      {/* {message.response.text} */}
                      <MarkupMessageRenderer message={message.response.text} />
                    {/* )} */}
                  </div>
                
                  {message.response?.products && (
                      // <div className="chat-message-response-products" >
                      <>
                      {message.response.products.length > 0 ? (
                        <div className="chat-message-response-products">
                          ✓ {message.response.products.length} product{message.response.products.length !== 1 ? 's' : ''} found
                        </div>
                      ) : (
                        <div className="chat-message-response-products">
                          ✗ No products found
                        </div>
                      )}
                      {/* </div> */}
                    </>
                  )}
                </div>
              )}
            </>
          ))
        }</div>
      )}
    </div>
  );
};

export default ChatHistory;